import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { GeolocateControl, GeolocateResultEvent, MapRef, ViewStateChangeEvent } from 'react-map-gl/maplibre';
import debounce from 'lodash.debounce';
import { useRouter } from 'next/router';
import { pickBy } from 'lodash';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';

import { MapWrapper } from '../../legacy';
import { useMapBoundingRadius } from '@restworld/utils-common';
import { Link } from '@restworld/ui-ds-base';

import { clusterLayer } from './map-layers';
import { JobPositionAndRestaurantPins, usePulsingDot } from './job-explorer-map.hooks';
import { useExplorerLoading } from '../job-explorer-content';
import { WorkerHomeLocation } from './worker-home-location';
import { explorerRestaurantListPayloadAtom } from '../job-explorer-restaurant-explorer.atoms';
import {
  getZoomLevelAtom,
  getCenterPointAtom,
  getToggleFullScreenAtom,
  getMapRefAtom,
  setBoundingRadiusAtom,
  setCenterPointAtom,
  setUserCoordinatesAtom,
} from '../job-explorer-map.atoms';
import { hasMapViewAtom, jobListParamsAtom } from '../job-explorer-list.atoms';
import { localityAddressAtom } from '../address-search-bar';
import { CustomMapWrapper, FullScreenCustomControl, MapSpinner } from './job-explorer-map.components';
import { useJobExplorerMap } from './job-explorer-map.hooks/use-explorer-map';

export type ClusterType =
  | GeoJSON.Feature<GeoJSON.Geometry>
  | GeoJSON.FeatureCollection<GeoJSON.Geometry>
  | GeoJSON.Geometry
  | string;

export const useOffset = () => useCallback((index: number) => index * 0.00001, []);

const useInitialViewState = () => {
  const zoomLevel = useAtomValue(getZoomLevelAtom);
  const centerPoint = useAtomValue(getCenterPointAtom);
  return useMemo(() => {
    const [longitude, latitude] = centerPoint;
    return { longitude, latitude, zoom: zoomLevel };
  }, [centerPoint, zoomLevel]);
};

const useInteractiveLayerIds = () => useMemo(() => (clusterLayer.id ? [clusterLayer.id] : []), []);

const useShallowChangeURLOnMove = () => {
  const router = useRouter();
  return useCallback(
    (evt: ViewStateChangeEvent) => {
      router.push(
        {
          query: {
            ...router.query,
            map_filters: JSON.stringify(
              pickBy(evt.viewState, (_, key) => ['longitude', 'latitude', 'zoom'].includes(key))
            ),
          },
        },
        null,
        { shallow: true }
      );
    },
    [router]
  );
};

const useOnMove = ({ mapRef }: { mapRef?: RefObject<MapRef | null> }) => {
  const { getRadius } = useMapBoundingRadius({ map: mapRef?.current });
  const setBoundingRadius = useSetAtom(setBoundingRadiusAtom);
  const setCenterPoint = useSetAtom(setCenterPointAtom);
  const setExplorerRestaurantListPayload = useSetAtom(explorerRestaurantListPayloadAtom);
  const setJobListParams = useSetAtom(jobListParamsAtom);

  return useCallback(
    (evt: ViewStateChangeEvent) => {
      const radius = getRadius({ lon: evt.viewState.longitude, lat: evt.viewState.latitude });
      setExplorerRestaurantListPayload({
        lon: evt.viewState.longitude,
        lat: evt.viewState.latitude,
        radius,
        offset: 0,
        limit: 3,
      });
      setBoundingRadius(radius);
      setJobListParams((p) => ({
        ...p,
        lon: evt.viewState.longitude,
        lat: evt.viewState.latitude,
        radius,
        offset: 0,
      }));
      setCenterPoint([evt.viewState.longitude, evt.viewState.latitude]);
    },
    [getRadius, setBoundingRadius, setCenterPoint, setExplorerRestaurantListPayload, setJobListParams]
  );
};

const GeoControl = () => {
  const setUserCoordinates = useSetAtom(setUserCoordinatesAtom);
  const onGeolocate = useCallback(
    (v: GeolocateResultEvent) => setUserCoordinates({ lat: v.coords.latitude, lon: v.coords.longitude }),
    [setUserCoordinates]
  );
  const isB2BAdvertisement = useIsB2BAdvertisement();
  const margin = isB2BAdvertisement ? '64px 16px 0 0' : '16px 16px 0px 0px';
  return (
    <GeolocateControl
      onGeolocate={onGeolocate}
      showUserLocation={false}
      positionOptions={{ enableHighAccuracy: true }}
      style={{
        margin,
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        borderRadius: '0.5rem',
      }}
      trackUserLocation
      position="top-right"
    />
  );
};

export const GettingStartedBanner = () => {
  const { t } = useTranslation('job-explorer-page');
  return (
    <div className="w-full px-2.5 py-1 left-0 top-0 absolute bg-purple-500 inline-flex flex-col justify-center items-center gap-2.5 [z-index: 1000]">
      <Link variant="reversed" href="/getting-started?utm_source=map">
        <h5 className="justify-start text-neutral-10 text-base font-medium">{t('publish_your_job_offer')}</h5>
      </Link>
    </div>
  );
};

export const useIsB2BAdvertisement = () => useRouter().query.ad === 'b2b';

export const JobExplorerMap = ({ isDesktop, testId }: { isDesktop: boolean; testId?: string }) => {
  const router = useRouter();
  const [isMapTouch, setIsMapTouch] = useState(false);

  const hasMapView = useAtomValue(hasMapViewAtom);
  const zoomLevel = useAtomValue(getZoomLevelAtom);
  const mapRef = useAtomValue(getMapRefAtom);
  const toggleFullScreen = useAtomValue(getToggleFullScreenAtom);
  const setLocalityAddress = useSetAtom(localityAddressAtom);

  const pulsingEl = usePulsingDot({ mapRef });
  const { handleZoomEndCb, onClick } = useJobExplorerMap();

  const initialViewState = useInitialViewState();
  const interactiveLayerIds = useInteractiveLayerIds();
  const shallowChangeURLOnMove = useShallowChangeURLOnMove();
  const onMove = useOnMove({ mapRef });
  const isB2BAdvertisement = useIsB2BAdvertisement();
  const explorerLoading = useExplorerLoading();

  useEffect(() => {
    mapRef.current?.on('moveend', () => {
      // Raw map events carry no react-map-gl viewState: read the values off the map itself.
      const map = mapRef.current?.getMap();
      if (!map) return;
      const center = map.getCenter();
      router.push(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            map_filters: JSON.stringify({ longitude: center.lng, latitude: center.lat, zoom: map.getZoom() }),
          },
        },
        null,
        { shallow: true }
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CustomMapWrapper data-cy={testId} onMouseEnter={() => setIsMapTouch(true)} toggleFullScreen={toggleFullScreen}>
      <MapWrapper
        cooperativeGestures={false}
        initialViewState={initialViewState}
        interactiveLayerIds={interactiveLayerIds}
        handleZoomEnd={handleZoomEndCb}
        onClick={onClick}
        zoom={zoomLevel}
        onMove={debounce(onMove, 700)}
        mapRef={mapRef}
        onDrag={debounce((evt) => {
          setLocalityAddress({});
          isMapTouch && shallowChangeURLOnMove(evt);
        }, 700)}
        onZoom={debounce((evt) => isMapTouch && shallowChangeURLOnMove(evt), 700)}
      >
        {isB2BAdvertisement && <GettingStartedBanner />}
        <JobPositionAndRestaurantPins />
        <GeoControl />
        <WorkerHomeLocation />
        {pulsingEl}
        {isDesktop && hasMapView && explorerLoading && <MapSpinner />}
        {isDesktop && <FullScreenCustomControl />}
      </MapWrapper>
    </CustomMapWrapper>
  );
};

export default JobExplorerMap;
